import { ChatLanguage, ChatSource, WidgetMessage } from './chat.types';

interface StoredChatHistory {
  version: 1;
  messages: WidgetMessage[];
  isBlocked: boolean;
  blockedUntil: string | null;
  blockedMessage: string | null;
  language: ChatLanguage | null;
}

export interface ConversationBlockState {
  isBlocked: boolean;
  blockedUntil: string | null;
  blockedMessage: string | null;
  hasExpired: boolean;
}

const STORAGE_KEY_PREFIX = 'pilgrim-chat-widget-history';
const STORAGE_VERSION = 1;

export class ChatHistoryService {
  getHistory(sessionId: string): WidgetMessage[] {
    return this._getStoredHistory(sessionId).messages;
  }

  saveHistory(sessionId: string, messages: WidgetMessage[]): void {
    const currentHistory = this._getStoredHistory(sessionId);

    this._saveHistory(
      sessionId,
      messages,
      currentHistory.isBlocked,
      currentHistory.blockedUntil,
      currentHistory.blockedMessage,
      currentHistory.language,
    );
  }

  getLanguage(sessionId: string): ChatLanguage | null {
    return this._getStoredHistory(sessionId).language;
  }

  setLanguage(sessionId: string, language: ChatLanguage): void {
    const currentHistory = this._getStoredHistory(sessionId);

    this._saveHistory(
      sessionId,
      currentHistory.messages,
      currentHistory.isBlocked,
      currentHistory.blockedUntil,
      currentHistory.blockedMessage,
      language,
    );
  }

  isConversationBlocked(sessionId: string): boolean {
    return this.getConversationBlockState(sessionId).isBlocked;
  }

  getConversationBlockState(sessionId: string): ConversationBlockState {
    const history = this._getStoredHistory(sessionId);
    const hasBlockDate = history.isBlocked && Boolean(history.blockedUntil);
    const isBlocked = hasBlockDate && this._isFutureDate(history.blockedUntil);

    return {
      isBlocked,
      blockedUntil: history.blockedUntil,
      blockedMessage: history.blockedMessage,
      hasExpired: hasBlockDate && !isBlocked,
    };
  }

  setConversationBlocked(
    sessionId: string,
    isBlocked: boolean,
    blockedUntil: string | null,
    blockedMessage: string | null,
  ): void {
    const currentHistory = this._getStoredHistory(sessionId);

    this._saveHistory(
      sessionId,
      currentHistory.messages,
      isBlocked,
      isBlocked ? blockedUntil : null,
      isBlocked ? blockedMessage : null,
      currentHistory.language,
    );
  }

  clearHistory(sessionId: string): void {
    try {
      localStorage.removeItem(this._getStorageKey(sessionId));
    } catch {
      return;
    }
  }

  private _saveHistory(
    sessionId: string,
    messages: WidgetMessage[],
    isBlocked: boolean,
    blockedUntil: string | null,
    blockedMessage: string | null,
    language: ChatLanguage | null,
  ): void {
    const history: StoredChatHistory = {
      version: STORAGE_VERSION,
      messages: messages.map((message) => ({ ...message })),
      isBlocked,
      blockedUntil,
      blockedMessage,
      language,
    };

    try {
      localStorage.setItem(this._getStorageKey(sessionId), JSON.stringify(history));
    } catch {
      return;
    }
  }

  private _getStoredHistory(sessionId: string): StoredChatHistory {
    const rawHistory = this._getRawHistory(sessionId);

    if (!rawHistory) {
      return this._createEmptyHistory();
    }

    try {
      return this._parseHistory(rawHistory);
    } catch {
      this.clearHistory(sessionId);
      return this._createEmptyHistory();
    }
  }

  private _getRawHistory(sessionId: string): string | null {
    try {
      return localStorage.getItem(this._getStorageKey(sessionId));
    } catch {
      return null;
    }
  }

  private _parseHistory(rawHistory: string): StoredChatHistory {
    const parsedHistory = JSON.parse(rawHistory) as unknown;
    const messages = this._getMessages(parsedHistory);
    const isBlocked = this._getIsBlocked(parsedHistory);
    const blockedUntil = this._getBlockedUntil(parsedHistory);
    const blockedMessage = this._getBlockedMessage(parsedHistory);
    const language = this._getLanguage(parsedHistory);

    return {
      version: STORAGE_VERSION,
      isBlocked,
      blockedUntil,
      blockedMessage,
      language,
      messages: messages
        .map((message) => this._normalizeWidgetMessage(message))
        .filter((message): message is WidgetMessage => Boolean(message)),
    };
  }

  private _getMessages(parsedHistory: unknown): unknown[] {
    if (Array.isArray(parsedHistory)) {
      return parsedHistory;
    }

    if (!this._isStoredHistory(parsedHistory)) {
      return [];
    }

    return parsedHistory.messages;
  }

  private _getIsBlocked(parsedHistory: unknown): boolean {
    if (!this._isStoredHistory(parsedHistory)) {
      return false;
    }

    return parsedHistory.isBlocked === true;
  }

  private _getBlockedUntil(parsedHistory: unknown): string | null {
    if (!this._isStoredHistory(parsedHistory) || typeof parsedHistory.blockedUntil !== 'string') {
      return null;
    }

    const parsedDate = new Date(parsedHistory.blockedUntil);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedHistory.blockedUntil;
  }

  private _getBlockedMessage(parsedHistory: unknown): string | null {
    if (!this._isStoredHistory(parsedHistory) || typeof parsedHistory.blockedMessage !== 'string') {
      return null;
    }

    const blockedMessage = parsedHistory.blockedMessage.trim();

    return blockedMessage || null;
  }

  private _getLanguage(parsedHistory: unknown): ChatLanguage | null {
    if (!this._isStoredHistory(parsedHistory) || typeof parsedHistory.language !== 'string') {
      return null;
    }

    return this._isSupportedLanguage(parsedHistory.language) ? parsedHistory.language : null;
  }

  private _isStoredHistory(value: unknown): value is StoredChatHistory {
    return (
      typeof value === 'object' &&
      value !== null &&
      'messages' in value &&
      Array.isArray(value.messages)
    );
  }

  private _isWidgetMessage(value: unknown): value is WidgetMessage {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const message = value as WidgetMessage;

    return (
      typeof message.id === 'string' &&
      (message.author === 'bot' || message.author === 'user') &&
      typeof message.content === 'string' &&
      typeof message.createdAt === 'string'
    );
  }

  private _normalizeWidgetMessage(value: unknown): WidgetMessage | null {
    if (!this._isWidgetMessage(value)) {
      return null;
    }

    const message: WidgetMessage = {
      id: value.id,
      author: value.author,
      content: value.content,
      createdAt: value.createdAt,
    };
    const sources = this._getSources(value);

    return sources.length ? { ...message, sources } : message;
  }

  private _getSources(message: WidgetMessage): ChatSource[] {
    if (!Array.isArray(message.sources)) {
      return [];
    }

    return message.sources.filter((source): source is ChatSource => this._isChatSource(source));
  }

  private _isChatSource(value: unknown): value is ChatSource {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const source = value as ChatSource;

    return (
      typeof source.label === 'string' &&
      Boolean(source.label.trim()) &&
      typeof source.url === 'string' &&
      this._isSafeUrl(source.url) &&
      (source.iconUrl === undefined ||
        (typeof source.iconUrl === 'string' && this._isSafeUrl(source.iconUrl)))
    );
  }

  private _isSafeUrl(value: string): boolean {
    try {
      const url = new URL(value);

      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private _isSupportedLanguage(value: string): value is ChatLanguage {
    return value === 'es' || value === 'en' || value === 'it' || value === 'fr' || value === 'de';
  }

  private _getStorageKey(sessionId: string): string {
    return `${STORAGE_KEY_PREFIX}:${sessionId}`;
  }

  private _isFutureDate(value: string | null): boolean {
    if (!value) {
      return false;
    }

    return new Date(value).getTime() > Date.now();
  }

  private _createEmptyHistory(): StoredChatHistory {
    return {
      version: STORAGE_VERSION,
      messages: [],
      isBlocked: false,
      blockedUntil: null,
      blockedMessage: null,
      language: null,
    };
  }
}
